import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import api from '../../services/api';

const AccountDashboard = () => {
  const { user } = useAuth();
  const { getTotalItems } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSpent: 0,
    pendingOrders: 0,
    deliveredOrders: 0
  });

  useEffect(() => {
    if (user) {
      fetchUserOrders();
    }
  }, [user]);

  const fetchUserOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/user/${user.id}`);
      const userOrders = response.data || [];
      setOrders(userOrders);
      
      // Calculer les statistiques
      const totalSpent = userOrders.reduce((sum, order) => {
        const amount = typeof order.grand_total === 'string' ? parseFloat(order.grand_total) : order.grand_total;
        return sum + (amount || 0);
      }, 0);
      
      setStats({
        totalOrders: userOrders.length,
        totalSpent: totalSpent,
        pendingOrders: userOrders.filter(o => o.status === 'pending' || o.status === 'confirmed' || o.status === 'preparing').length,
        deliveredOrders: userOrders.filter(o => o.status === 'delivered').length
      });
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-600',
      confirmed: 'bg-blue-100 text-blue-600',
      preparing: 'bg-purple-100 text-purple-600',
      shipped: 'bg-indigo-100 text-indigo-600',
      delivered: 'bg-green-100 text-green-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'Préparation',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return <span className={`px-2 py-1 rounded-full text-xs ${badges[status] || 'bg-gray-100 text-gray-600'}`}>{labels[status] || status}</span>;
  };

  if (!user) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Connectez-vous</h2>
        <p className="text-gray-600 mb-8">Accédez à votre espace personnel</p>
        <Link to="/login" className="btn-primary inline-block">Se connecter</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Mon compte</h1>
          <p className="text-gray-500 mt-1">Bienvenue, {user.firstName} {user.lastName}</p>
        </div>
        <div className="flex gap-3">
          <Link to="/account/orders" className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition">
            Mes commandes
          </Link>
          <Link to="/account/addresses" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition">
            Mes adresses
          </Link>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="text-2xl font-bold text-primary">{stats.totalOrders}</p>
          <p className="text-sm text-gray-500">Commandes</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 text-center">
          <p className="text-2xl font-bold text-primary">{stats.totalSpent.toFixed(2)} €</p>
          <p className="text-sm text-gray-500">Total dépensé</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 text-center border-l-4 border-yellow-500">
          <p className="text-2xl font-bold text-yellow-600">{stats.pendingOrders}</p>
          <p className="text-sm text-gray-500">En cours</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5 text-center border-l-4 border-green-500">
          <p className="text-2xl font-bold text-green-600">{stats.deliveredOrders}</p>
          <p className="text-sm text-gray-500">Livrées</p>
        </div>
      </div>

      {/* Dernières commandes */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-5 border-b">
          <h2 className="text-xl font-bold">Dernières commandes</h2>
        </div>
        {orders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>Aucune commande pour le moment</p>
            <Link to="/products" className="btn-primary inline-block mt-4">Découvrir nos produits</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">N° Commande</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Total</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {orders.slice(0, 5).map(order => (
                  <tr key={order.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm font-bold">{order.order_number}</td>
                    <td className="px-4 py-3">{new Date(order.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 font-bold">{order.grand_total} €</td>
                    <td className="px-4 py-3">{getStatusBadge(order.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Informations personnelles */}
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="bg-white rounded-xl shadow-md p-5">
          <h3 className="font-bold text-lg mb-3">Informations personnelles</h3>
          <div className="space-y-2">
            <p><span className="font-medium">Nom :</span> {user.firstName} {user.lastName}</p>
            <p><span className="font-medium">Email :</span> {user.email}</p>
            <p><span className="font-medium">Rôle :</span> {user.role === 'admin' ? 'Administrateur' : 'Client'}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <h3 className="font-bold text-lg mb-3">Actions rapides</h3>
          <div className="space-y-3">
            <Link to="/products" className="block bg-primary text-white text-center py-2 rounded-lg hover:bg-opacity-90 transition">
              Commander maintenant
            </Link>
            <Link to="/cart" className="block bg-gray-200 text-gray-700 text-center py-2 rounded-lg hover:bg-gray-300 transition">
              Voir mon panier ({getTotalItems()} articles)
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;